import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import type {
  EditTripSegment,
  EditTripStep,
} from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import {
  isHotelIngredient,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { ActivityIngredient } from '@apps/main/modules/recipe/ingredients';

import { SearchTermType } from '@libs/trip-planner-interfaces';
import type { IngredientId } from '@libs/trip-planner-interfaces';
import { trackNightsChanged } from '@libs/google-analytics-events';

import { useRecipeStore } from '../recipe/recipeStore';
import { useMapStore } from '../mapStore/mapStore';

import {
  getEditTripSegmentFromStayIngredient,
  getNewRecipeOrder,
  updateRecipeTransportPorts,
} from './editTripStoreLogic';

export const useEditTripStore = defineStore('EditTripStore', () => {
  const recipeStore = useRecipeStore();
  const mapStore = useMapStore();

  // State
  const DEFAULT_INITIAL_STEP: EditTripStep = {
    stepType: EditTripStepType.Overview,
    isInitialStep: true,
  };

  const initialStep = ref<EditTripStep>(DEFAULT_INITIAL_STEP);

  // Actions
  function resetInitialStep(): void {
    initialStep.value = DEFAULT_INITIAL_STEP;
  }

  function changeTripOrder(segments: EditTripSegment[]): void {
    const newRecipe = getNewRecipeOrder(segments, recipeStore.recipe);

    const updatedRecipe = updateRecipeTransportPorts(
      newRecipe,
      mapStore.startingLocation?.position
    );

    recipeStore.updateRecipe(updatedRecipe);
  }

  function changeNumberOfNights(
    ingredientId: IngredientId,
    numberOfNights: number | undefined
  ): void {
    const stayIngredient = recipeStore.getStayIngredient(ingredientId);
    if (!stayIngredient) {
      return;
    }
    trackNightsChanged();
    stayIngredient.numberOfNights = numberOfNights;

    if (isHotelIngredient(stayIngredient) && numberOfNights === 0) {
      removeAccommodationFromHotelIngredient(ingredientId);
    }
  }

  function removeAccommodationFromHotelIngredient(
    hotelIngredientId: IngredientId
  ): void {
    const hotelIngredient = recipeStore.getHotelIngredient(hotelIngredientId);

    const destinationPolygonText =
      hotelIngredient.nearbyPolygon?.name ??
      hotelIngredient.commonPolygon?.name;

    const searchTerm = hotelIngredient.searchTerm;
    if (searchTerm.type === SearchTermType.FreeText) {
      searchTerm.freeText = destinationPolygonText ?? searchTerm.freeText;
    } else if (searchTerm.type === SearchTermType.PolygonId) {
      const destinationPolygonId =
        hotelIngredient.nearbyPolygon?.id ?? hotelIngredient.commonPolygon?.id;

      searchTerm.polygonId = destinationPolygonId ?? searchTerm.polygonId;
    }

    const ownStayIngredient = new OwnStayIngredient(searchTerm);
    ownStayIngredient.displayText =
      destinationPolygonText ?? hotelIngredient.displayText;
    ownStayIngredient.numberOfNights = hotelIngredient.numberOfNights;
    ownStayIngredient.transportTypeToBeReached =
      hotelIngredient.transportTypeToBeReached;
    ownStayIngredient.subIngredients = hotelIngredient.subIngredients.map(
      (activitySubIngredient) => {
        const newActivitySubIngredient = new ActivityIngredient(
          activitySubIngredient.code,
          ownStayIngredient.ingredientId
        );

        newActivitySubIngredient.offset = activitySubIngredient.offset;
        newActivitySubIngredient.tickets = activitySubIngredient.tickets;

        return newActivitySubIngredient;
      }
    );

    recipeStore.replaceIngredient(hotelIngredient, ownStayIngredient);
  }

  // Getters
  const modalData = computed<EditTripSegment[]>((): EditTripSegment[] =>
    recipeStore.recipe
      .filter(isStayIngredient)
      .map((ingredient) =>
        getEditTripSegmentFromStayIngredient(
          ingredient,
          mapStore.calculatedRoute?.[recipeStore.recipe.indexOf(ingredient)]
        )
      )
  );

  return {
    // State
    initialStep,

    // Actions
    resetInitialStep,
    changeTripOrder,
    changeNumberOfNights,
    removeAccommodationFromHotelIngredient,

    // Getters
    modalData,
  };
});
